/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { isAdvancedPromptManagementEnabled } from '../../../common/prompt/promptManagementFlag';
import {
  AGENTIC_PROMPT_MGMT_ROUTE,
  LEGACY_PROMPT_MGMT_ROUTE,
  MODULE_SWITCHER_VALUE_AGENTIC,
  MODULE_SWITCHER_VALUE_LEGACY,
  VIEW_TYPE_PROMPT_MANAGEMENT
} from '../constants/AgenticPromptMgmtConstants';
import { RUBRICS_TAB_PATH, RUBRICS_TAB_TITLE } from '../constants/RubricsConstants';

export const LEGACY_PROMPT_DASHBOARD_TAB_NAME = 'Legacy Prompt Management';

/** Display copy when advanced PMS is OFF — same as agentic Prompt Management card. */
export const PROMPT_MANAGEMENT_DASHBOARD_TAB_NAME = 'Prompt Management';
export const PROMPT_MANAGEMENT_DASHBOARD_DESCRIPTION =
  'Full lifecycle, from versioning and updates to production rollouts.';

export const normalizePath = (path = '') => {
  if (!path) {
    return '';
  }
  return path.startsWith('/') ? path : `/${path}`;
};

export const sanitizeNavigationPath = (path = '') => {
  const normalized = normalizePath(path);
  if (!normalized || normalized.includes('..')) {
    return '';
  }
  return normalized;
};

export const findRubricsTab = (tabDetails) =>
  tabDetails?.find((tab) => tab.tabTitle === RUBRICS_TAB_TITLE || normalizePath(tab.path) === RUBRICS_TAB_PATH) || null;

const isAgenticPromptTab = (tab) =>
  Boolean(
    tab &&
      (tab.viewType === VIEW_TYPE_PROMPT_MANAGEMENT ||
        tab.tabUrl?.includes(AGENTIC_PROMPT_MGMT_ROUTE) ||
        tab.path?.includes(AGENTIC_PROMPT_MGMT_ROUTE) ||
        tab.tabTitle === AGENTIC_PROMPT_MGMT_ROUTE)
  );

export const findAgenticPromptTab = (tabDetails) => tabDetails?.find((tab) => isAgenticPromptTab(tab)) || null;

const isLegacyPromptTab = (tab) =>
  Boolean(
    tab &&
      (normalizePath(tab.path) === `/${LEGACY_PROMPT_MGMT_ROUTE}` ||
        normalizePath(tab.path).endsWith(`/${LEGACY_PROMPT_MGMT_ROUTE}`) ||
        tab.tabTitle === LEGACY_PROMPT_MGMT_ROUTE ||
        tab.tabUrl === LEGACY_PROMPT_MGMT_ROUTE ||
        tab.tabName === LEGACY_PROMPT_DASHBOARD_TAB_NAME)
  );

/**
 * Dashboard / drawer tabs for the active tenant.
 * Flag ON: hide Legacy Prompt Management card (agentic remains).
 * Flag OFF: hide agentic Prompt Management; show Legacy when present,
 * relabeled with Prompt Management name/description for display.
 * @param {Array} tabDetails
 * @param {{ enableAdvancedPromptManagement?: boolean }|null|undefined} tenant
 */
export const filterDashboardTabs = (tabDetails, tenant) => {
  const tabs = tabDetails ?? [];
  if (isAdvancedPromptManagementEnabled(tenant)) {
    return tabs.filter((item) => item.tabName !== LEGACY_PROMPT_DASHBOARD_TAB_NAME);
  }
  const agenticTab = findAgenticPromptTab(tabs);
  const displayName = agenticTab?.tabName || PROMPT_MANAGEMENT_DASHBOARD_TAB_NAME;
  const displayDescription = agenticTab?.description || PROMPT_MANAGEMENT_DASHBOARD_DESCRIPTION;
  return tabs
    .filter((item) => !isAgenticPromptTab(item))
    .map((item) => {
      if (!isLegacyPromptTab(item)) {
        return item;
      }
      return {
        ...item,
        tabName: displayName,
        description: displayDescription
      };
    });
};

export const findLegacyPromptTab = (tabDetails) => tabDetails?.find((tab) => isLegacyPromptTab(tab)) || null;

/**
 * Safe redirect when agentic PMS is unavailable for the tenant (flag OFF).
 * Prefers Legacy Prompt Management; otherwise tenant dashboard.
 */
export const resolveAgenticPromptFallbackPath = (tenant) => {
  const tenantName = tenant?.tenantName || tenant?.name;
  if (!tenantName) {
    return '/aiadmin';
  }
  const legacyTab = findLegacyPromptTab(tenant?.tabDetails);
  if (legacyTab?.path) {
    return `/aiadmin/${tenantName}${sanitizeNavigationPath(legacyTab.path)}`;
  }
  return `/aiadmin/${tenantName}`;
};

export const getSharedModuleSwitcher = (tabDetails) =>
  tabDetails?.find((tab) => tab?.moduleSwitcher?.enabled)?.moduleSwitcher || null;

export const resolveModuleSwitcherConfig = (tabDetails, activeTab) => {
  if (activeTab?.moduleSwitcher?.enabled) {
    return activeTab.moduleSwitcher;
  }

  return getSharedModuleSwitcher(tabDetails);
};

const stripTenantAdminPrefix = (pathname, tenantName) => {
  if (!pathname) {
    return '';
  }
  if (!tenantName) {
    return pathname;
  }
  const prefix = `/aiadmin/${tenantName}`;
  if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
    const suffix = pathname.slice(prefix.length);
    return suffix || '/';
  }
  return pathname;
};

const pathnameMatchesOption = (pathname, optionPath, tenantName) => {
  const normalizedOptionPath = sanitizeNavigationPath(optionPath);
  if (!normalizedOptionPath || !pathname) {
    return false;
  }
  const routePath = normalizePath(stripTenantAdminPrefix(pathname, tenantName));
  return routePath === normalizedOptionPath || routePath.startsWith(`${normalizedOptionPath}/`);
};

export const resolveModuleSwitcherValue = (moduleSwitcher, pathname, tenantName) => {
  if (!moduleSwitcher?.options?.length) {
    return moduleSwitcher?.defaultValue || MODULE_SWITCHER_VALUE_AGENTIC;
  }

  const matchedOption = moduleSwitcher.options.find((option) =>
    pathnameMatchesOption(pathname, option.path, tenantName)
  );
  if (matchedOption?.value != null) {
    return matchedOption.value;
  }

  return moduleSwitcher.defaultValue || MODULE_SWITCHER_VALUE_AGENTIC;
};

export const resolveModuleSwitcherOption = (moduleSwitcher, value) =>
  moduleSwitcher?.options?.find((option) => option.value === value) || null;

export const buildModuleSwitcherRoute = (tenantName, optionPath) => {
  if (!tenantName || !optionPath) {
    return '';
  }
  return `/aiadmin/${tenantName}${sanitizeNavigationPath(optionPath)}`;
};

export const isAgenticPromptRoute = (pathname, tenantName) =>
  pathnameMatchesOption(pathname, AGENTIC_PROMPT_MGMT_ROUTE, tenantName);

export const isLegacyPromptRoute = (pathname, tenantName) =>
  pathnameMatchesOption(pathname, LEGACY_PROMPT_MGMT_ROUTE, tenantName) && !isAgenticPromptRoute(pathname, tenantName);

/**
 * Show legacy ↔ agentic switcher only when advanced PMS is ON and both tabs exist.
 * @param {object} moduleSwitcher
 * @param {Array} tabDetails
 * @param {{ enableAdvancedPromptManagement?: boolean }|null|undefined} tenant
 */
export const shouldShowPromptModuleSwitcher = (moduleSwitcher, tabDetails, tenant) => {
  if (!isAdvancedPromptManagementEnabled(tenant)) {
    return false;
  }
  if (!moduleSwitcher?.enabled || !moduleSwitcher.options?.length) {
    return false;
  }

  const legacyTab = findLegacyPromptTab(tabDetails);
  const agenticTab = findAgenticPromptTab(tabDetails);
  return Boolean(legacyTab && agenticTab);
};

/**
 * When advanced PMS is OFF, strip agentic options so switcher cannot navigate there.
 */
export const resolvePromptModuleSwitcherForTenant = (moduleSwitcher, tenant) => {
  if (!moduleSwitcher) {
    return null;
  }
  if (isAdvancedPromptManagementEnabled(tenant)) {
    return moduleSwitcher;
  }
  const options = (moduleSwitcher.options || []).filter((option) => option?.value !== MODULE_SWITCHER_VALUE_AGENTIC);
  if (!options.length) {
    return { ...moduleSwitcher, enabled: false, options };
  }
  return {
    ...moduleSwitcher,
    enabled: false,
    defaultValue: MODULE_SWITCHER_VALUE_LEGACY,
    options
  };
};
